import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import AuthLayout from "../layouts/AuthLayout";
import { userService } from "../../_services";
import Signupleft from "../../images/signupleft.webp";
import signupcheck from "../../images/savecheck.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

function RuserSetup() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        Fname: '',
        Lname: '',
        Phone: '',
        Email: '',
        UserName: '',
        Password: '',
        ConfirmPassword: '',
    });
    const [dataValidated, setDataValidated] = useState(false);
    const [loadingText, setLoadingText] = useState('Please wait while your request is processing');
    const [userId, setUserId] = useState('');
    const [accessCode, setAccessCode] = useState('');
    const [passcode, setPasscode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userUpdated, setUserUpdated] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const params = new URL(window.location.href).searchParams;
        const token = params.get('accesscode');
        const username = params.get('username');
        const pc = params.get('passcode');
        setAccessCode(token);
        setPasscode(pc);
        verifyUser(token, username, pc);
    }, []);

    const verifyUser = async (token, username, pc) => {
        try {
            const apidata = await userService.verifyuser_organic(token, username, pc);
            if (apidata.length > 0 && apidata[0].statusMessage === "Success") {
                const value = apidata[0];
                setFormData(prev => ({
                    ...prev,
                    Fname: value.fname || '',
                    Lname: value.lname || '',
                    Phone: value.phone || '',
                    Email: value.email || '',
                }));
                setUserId(value.userId || '');
                setDataValidated(true);
            } else {
                setDataValidated(false);
                setLoadingText("Invalid Request! Please Click below to signup");
            }
        } catch {
            setDataValidated(false);
            setLoadingText("Invalid Request! Please Click below to signup");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "Fname" || name === "Lname") {
            if (value !== "" && !/^[a-zA-Z]+$/.test(value)) return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreeTerms) return;

        const newErrors = {};
        const trimmedUsername = formData.UserName.trim();

        // Async: username filter check
        try {
            const usernameCheck = await userService.GetUserNameFilter(trimmedUsername);
            if (usernameCheck.length > 0 && usernameCheck[0].code !== '0') {
                newErrors.UserName = usernameCheck[0].message;
            }
        } catch { /* ignore */ }

        // Async: phone validation
        try {
            const phoneCheck = await userService.GetPhoneNoValidate(formData.Phone);
            if (phoneCheck[0].code !== "1") {
                newErrors.Phone = 'Invalid Phone Number';
            } else if (phoneCheck[0].message) {
                setFormData(prev => ({ ...prev, Phone: phoneCheck[0].message }));
            }
        } catch { /* ignore */ }

        // Sync validations
        if (formData.Fname.length < 3) newErrors.Fname = 'First Name must be at least 3 characters long';
        if (formData.Lname.length < 2) newErrors.Lname = 'Last Name must be at least 2 characters long';
        if (formData.Phone.replace(/[- )(]/g, '').length !== 10) newErrors.Phone = 'Phone Number must be 10 digits';
        if (formData.Email.length < 1) newErrors.Email = 'Enter your email';
        if (!userService.ValidEmailRegex(formData.Email) && formData.Email !== '') newErrors.Email = 'Invalid Email';
        if (trimmedUsername.length < 6) newErrors.UserName = 'Username must be at least 6 characters long';
        if (['user', 'rxh', 'temp'].some(kw => trimmedUsername.includes(kw))) newErrors.UserName = 'This Username not allowed';
        if (/^[0-9]+$/.test(trimmedUsername)) newErrors.UserName = 'This Username not allowed';
        if (trimmedUsername.includes(' ')) newErrors.UserName = 'Username does not allow space';
        if (formData.Password.length < 6) newErrors.Password = 'Password must be at least 6 characters long';
        if (formData.Password !== formData.ConfirmPassword) newErrors.ConfirmPassword = 'Password & Confirm Password must match';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);
        try {
            const apidata = await userService.update_orguser(
                userId,
                trimmedUsername,
                accessCode,
                passcode,
                formData.Fname,
                formData.Lname,
                formData.Email,
                formData.Phone,
                formData.Password
            );
            if (apidata.length > 0) {
                if (apidata[0].code !== '-1') {
                    setUserUpdated(true);
                } else {
                    if (apidata[0].statusMessage === 'Phone no is already exist') {
                        setErrors({ Phone: 'Phone Number already exists' });
                        setFormData(prev => ({ ...prev, Phone: '' }));
                    } else {
                        setErrors({ UserName: apidata[0].statusMessage });
                        setFormData(prev => ({ ...prev, UserName: '' }));
                    }
                }
            }
        } catch (error) {
            console.error('Update error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Redirect if already logged in
    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/Affiliate_Url" replace />;
    }

    // ── Success screen ──────────────────────────────────────────────────────────
    if (userUpdated) {
        return (
            <AuthLayout imageSrc={Signupleft}>
                <Card className="border-0 shadow-none">
                    <CardContent className="pt-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <img src={signupcheck} alt="Success" className="w-20 h-20" />
                            <p className="text-lg font-semibold">Updated successfully</p>
                            <Button onClick={() => navigate('/login')} className="w-full">
                                Sign In Now
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    // ── Loading / Invalid screen ────────────────────────────────────────────────
    if (!dataValidated) {
        return (
            <AuthLayout imageSrc={Signupleft}>
                <Card className="border-0 shadow-none">
                    <CardContent className="pt-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            {loadingText === "Please wait while your request is processing" && (
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            )}
                            <p className="text-lg">{loadingText}</p>
                            {loadingText !== "Please wait while your request is processing" && (
                                <Button onClick={() => navigate('/signup')} className="w-full">
                                    Sign Up here
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </AuthLayout>
        );
    }

    // ── Main form ───────────────────────────────────────────────────────────────
    return (
        <AuthLayout imageSrc={Signupleft}>
            <Card className="border-0 shadow-none">
                <CardHeader>
                    <CardTitle>Set Details</CardTitle>
                    <CardDescription>Complete your account setup</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">

                        {/* First Name */}
                        <div className="space-y-2">
                            <Label htmlFor="Fname">First Name</Label>
                            <Input
                                id="Fname"
                                name="Fname"
                                value={formData.Fname}
                                onChange={handleInputChange}
                                className={cn(errors.Fname && "border-destructive")}
                            />
                            {errors.Fname && <p className="text-sm text-destructive">{errors.Fname}</p>}
                        </div>

                        {/* Last Name */}
                        <div className="space-y-2">
                            <Label htmlFor="Lname">Last Name</Label>
                            <Input
                                id="Lname"
                                name="Lname"
                                value={formData.Lname}
                                onChange={handleInputChange}
                                className={cn(errors.Lname && "border-destructive")}
                            />
                            {errors.Lname && <p className="text-sm text-destructive">{errors.Lname}</p>}
                        </div>

                        {/* Phone */}
                        <div className="space-y-2">
                            <Label htmlFor="Phone">Phone Number</Label>
                            <Input
                                id="Phone"
                                name="Phone"
                                value={formData.Phone}
                                onChange={handleInputChange}
                                className={cn(errors.Phone && "border-destructive")}
                            />
                            {errors.Phone && <p className="text-sm text-destructive">{errors.Phone}</p>}
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="Email">Email</Label>
                            <Input
                                id="Email"
                                name="Email"
                                type="email"
                                value={formData.Email}
                                onChange={handleInputChange}
                                className={cn(errors.Email && "border-destructive")}
                            />
                            {errors.Email && <p className="text-sm text-destructive">{errors.Email}</p>}
                        </div>

                        {/* Username */}
                        <div className="space-y-2">
                            <Label htmlFor="UserName">Username</Label>
                            <Input
                                id="UserName"
                                name="UserName"
                                value={formData.UserName}
                                onChange={handleInputChange}
                                className={cn(errors.UserName && "border-destructive")}
                            />
                            {errors.UserName && <p className="text-sm text-destructive">{errors.UserName}</p>}
                        </div>

                        {/* Password */}
                        <div className="space-y-2">
                            <Label htmlFor="Password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="Password"
                                    name="Password"
                                    type={showPassword ? "text" : "password"}
                                    value={formData.Password}
                                    onChange={handleInputChange}
                                    maxLength={30}
                                    className={cn(errors.Password && "border-destructive", "pr-10")}
                                />
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                            {errors.Password && <p className="text-sm text-destructive">{errors.Password}</p>}
                        </div>

                        {/* Confirm Password */}
                        <div className="space-y-2">
                            <Label htmlFor="ConfirmPassword">Confirm Password</Label>
                            <div className="relative">
                                <Input
                                    id="ConfirmPassword"
                                    name="ConfirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={formData.ConfirmPassword}
                                    onChange={handleInputChange}
                                    maxLength={30}
                                    className={cn(errors.ConfirmPassword && "border-destructive", "pr-10")}
                                />
                                <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                            {errors.ConfirmPassword && <p className="text-sm text-destructive">{errors.ConfirmPassword}</p>}
                        </div>

                        {/* Terms & Conditions */}
                        <div className="flex items-start space-x-2">
                            <Checkbox
                                id="terms"
                                checked={agreeTerms}
                                onCheckedChange={setAgreeTerms}
                            />
                            <label htmlFor="terms" className="text-sm leading-tight cursor-pointer">
                                By clicking submit you agree to our{' '}
                                <a href="/terms/" target="_blank" rel="noreferrer" className="text-primary hover:no-underline hover:text-foreground">
                                    Terms and Conditions
                                </a>
                                .
                            </label>
                        </div>

                        {/* Submit */}
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={!agreeTerms || isLoading}
                        >
                            {isLoading
                                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting</>
                                : 'Submit'
                            }
                        </Button>

                    </form>
                </CardContent>
            </Card>
        </AuthLayout>
    );
}

export default RuserSetup;
